import { map, isEmpty, join, head, isEqual, noop } from 'lodash/fp';
import { pipe } from 'fp-ts/function';
import { array, option, string } from 'fp-ts';
import { createAction, DateTimePart, DateTimeUtcService, showSuccessAlert, lt } from '@mews/framework';
import {
    EntityDeletionState,
    MultipleReservationConfirmationParameters,
    ResourceState,
    ServiceOrderState,
} from '@mews-commander/data-access';
import { TextKey, RouteName } from '@mews-commander/enums';
import { replace } from '@mews/routing';
import { ResourceStateReasonClassification } from '@mews-commander/resources';
import { AsyncAction, CommanderThunkAction, WithEnterpriseId, WithGroupId, WithScope } from '@mews-commander/types';
import { CancellationReason, ReservationPurpose } from '@mews-commander/legacy-data-structures';
import { getItemByCommonPropertyValue } from '@mews/fp';
import {
    getDateTimeUtc,
    createFetchAction,
    sendControllerRequest,
    getFirstFile,
    notEmpty,
    mapId,
    openInBrowserTab,
    setUrlParams,
    getNowUtc,
} from '@mews-commander/utils';
import { getCurrentEnterpriseId } from '@mews-commander/core';
import { LocalizedDateFromUtc, LocalizedShortTimeFromUtc } from '@mews-commander/ui';
import { fetchCardsByAccountIds } from '@mews-commander/legacy-entities/src/actions/paymentCards';
import {
    fetchCurrentEnterpriseReservationsByGroupIds,
    fetchCurrentEnterpriseReservationsByReservationIds,
    updateCurrentEnterpriseReservations,
} from '@mews-commander/legacy-entities/src/actions/reservations';
import {
    addProductOrder,
    getReservationsGroupServiceIds,
} from '@mews-commander/legacy-entities/src/ducks/reservations';
import {
    getCurrentEnterpriseIanaTimeZoneIdentifier,
    getCurrentEnterpriseEditableHistoryIntervalMilliseconds,
} from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import {
    fetchCurrentEnterpriseTasksByServiceOrderIds,
    closeTask as closeTaskAction,
} from '@mews-commander/legacy-entities/src/actions/tasks';
import { openPassportScannerInBrowserTab } from '@mews-commander/legacy-entities/src/ducks/passportScannerIntegrations';
import { deleteCompanionship as deleteCompanionshipAction } from '@mews-commander/legacy-entities/src/ducks/companionships';
import {
    getCurrentEnterpriseReservationIdsByGroupId,
    getCurrentEnterpriseReservationsAssignedResourceIds,
    isAnyCurrentEnterpriseReservationCancelled,
} from '@mews-commander/legacy-entities/src/selectors/reservations';
import { fetchAccountNotes } from '@mews-commander/legacy-entities/src/actions/accountNotes';
import { fetchCurrentEnterpriseBillsByIds } from '@mews-commander/legacy-entities/src/actions/bills';
import { getReservationsOrderItemsBillIds } from '@mews-commander/legacy-entities/src/selectors/orderItems';
import { fetchRates } from '@mews-commander/legacy-entities/src/ducks/rates';
import { fetchAllEnterpriseServices } from '@mews-commander/legacy-entities/src/actions/services';
import { fetchKeyCutters } from '@mews-commander/legacy-entities/src/actions/keyCutters';
import { updateResourcesState } from '@mews-commander/legacy-entities/src/actions/resources';
import { getResourcesByIds } from '@mews-commander/legacy-entities/src/ducks/resources';
import { getCurrentDateTimeLocalizationInfo } from '@mews-commander/core/legacy-globalization';
import { openModal } from '../../app/actions/common';
import { getPlatformPrimaryUrl } from '../../app/reducers/configuration';
import {
    TASKS_FETCH_STARTED,
    TASKS_FETCH_SUCCEEDED,
    TASKS_FETCH_FAILED,
    PASSPORT_SCANNER_COMMAND_FETCH,
    RESERVATION_MEMBERSHIP_UPLOAD,
    ORDER_ITEMS_CANCEL,
    UNDO_CHECK_IN,
    UNDO_CHECK_OUT,
    RESERVATION_CONFIRMED,
    RESERVATION_CANCELLED,
    CHECK_OUT,
    RESERVATION_TOGGLE_CHECKED,
    RESERVATIONS_SET_CHECKED,
    RESERVATION_TOGGLE_SHOW_ALL_NOTES,
    SET_NOTE_IDS_TO_BE_DELETED,
    TASKS_FETCH,
    RESERVATION_TOGGLE_OPENED,
    RESERVATIONS_SET_OPENED,
    RESERVATION_LOAD_CANCELED,
    RESERVATION_STATE_CLEANED,
    ORDER_ITEMS_FETCH,
    RESERVATIONS_FETCH,
} from '../actionTypes';
import { RESERVATION_FULL_DETAIL_EXTENT, RESERVATION_ORDER_ITEMS_EXTENT, RESERVATION_DATA_EXTENT } from '../constants';
import { COVID_19_LABEL, COVID_19_VALUE } from '../components/StateTab/CancellationForm/constants';
import { getCheckedReservationIds, areCanceledReservationDataLoaded, getCheckedReservations } from '../selectors';
import { getReservationsCommonProductIds } from '../selectors/orderItems';
import { getReservationUrl } from '../../../router/selectors';
import { getCheckedReservationCustomerIds } from '../components/CustomersTable/selectors';
import { getBillingAccountUrl } from '../../navigation/billing';
import { fetchCustomerBalances, getLoadedBalancesCustomerIds } from '../ducks/balances';
import { CheckOutFormValues } from '../components/StateTab/CheckOutForm/types';
import { getCurrentEnterpriseTimelineUrl } from '../../timeline/selectors/view';
import { showTimeline } from '../../timeline/routes';
import { compressToBase64 } from '../../app/components/TemplateFetcher/TemplateFetcher';
import { trackReservationModuleEvent } from './tracking';

export const submitAddProductForm =
    ({ productId, count, date, time }: $TSFixMe, reservationIds: $TSFixMe): CommanderThunkAction =>
    (dispatch, getState, { container }) => {
        const { appLocalizationComponent } = container();

        const timezone = getCurrentEnterpriseIanaTimeZoneIdentifier(getState());
        const localizationInfo = getCurrentDateTimeLocalizationInfo(getState());
        const consumedUtc = date && time && getDateTimeUtc({ timezone, localizationInfo, date, time });

        return dispatch(
            addProductOrder({
                reservationIds,
                productId,
                count: appLocalizationComponent.parseNumber(count),
                consumedUtc,
            })
        );
    };

export const fetchTasks =
    (ids: $TSFixMe): CommanderThunkAction =>
    async (dispatch) => {
        const promise = dispatch(fetchCurrentEnterpriseTasksByServiceOrderIds(ids));
        return dispatch(createFetchAction(TASKS_FETCH, promise));
    };

export const closeTask =
    (id: $TSFixMe): CommanderThunkAction =>
    (dispatch) => {
        dispatch(createAction(TASKS_FETCH_STARTED));
        dispatch(closeTaskAction(id))
            .then(() => {
                showSuccessAlert({ description: lt('TaskCloseSuccessMessage') });
                dispatch(createAction(TASKS_FETCH_SUCCEEDED));
            })
            .catch((error: $TSFixMe) => {
                dispatch(createAction(TASKS_FETCH_FAILED));
                return error;
            });
    };

export const uploadReservationMembershipFile =
    (values: $TSFixMe): CommanderThunkAction<AsyncAction> =>
    async (dispatch) => {
        const platformPrimaryUrl = getPlatformPrimaryUrl();

        const { groupId, profileId } = values;
        const file = getFirstFile(values);

        const data = new window.FormData();
        // @ts-expect-error $TSFixMeError: Argument of type 'unknown' is not assignable to parameter of type 'string | Blob'.
        data.append('file', file);
        data.append('id', groupId);
        data.append('profileId', profileId);

        const url = `${platformPrimaryUrl}/Commander/ReservationMembership/Install?Layout=Empty`;
        const promise = sendControllerRequest(url, data);

        return dispatch(createFetchAction(RESERVATION_MEMBERSHIP_UPLOAD, promise));
    };

export const scan =
    (scannerId: $TSFixMe, reservationIds: $TSFixMe): CommanderThunkAction =>
    async (dispatch, getState, { container }) => {
        const { passportScannerComponent } = container();
        const promise = passportScannerComponent
            .addCommand(scannerId, reservationIds)
            .then(() => showSuccessAlert({ description: lt('PassportScannerCommandsCreated') }));
        return dispatch(createFetchAction(PASSPORT_SCANNER_COMMAND_FETCH, promise));
    };

export const redirect = (scannerUrl: $TSFixMe, reservationIds: $TSFixMe) =>
    openPassportScannerInBrowserTab(scannerUrl, head(reservationIds));

export const cancelOrderItems =
    (orderItemIds: $TSFixMe): CommanderThunkAction<AsyncAction> =>
    async (dispatch, getState, { container }) => {
        const enterpriseId = getCurrentEnterpriseId();
        const promise = container().orderItemComponent.cancel({ enterpriseId, orderItemIds });
        return dispatch(createFetchAction(ORDER_ITEMS_CANCEL, promise));
    };

export const undoCheckIn =
    (reservationIds: $TSFixMe, reason: $TSFixMe): CommanderThunkAction =>
    async (dispatch, getState, { container }) => {
        const { reservationComponent } = container();
        const promise = reservationComponent.undoCheckIn(reservationIds, reason);
        return dispatch(createFetchAction(UNDO_CHECK_IN, promise));
    };

export const undoCheckOut =
    (reservationIds: $TSFixMe, reason: $TSFixMe): CommanderThunkAction =>
    async (dispatch, getState, { container }) => {
        const { reservationComponent } = container();
        const promise = reservationComponent.undoCheckOut(reservationIds, reason);
        return dispatch(createFetchAction(UNDO_CHECK_OUT, promise));
    };

export const confirmReservation =
    (
        params: Pick<MultipleReservationConfirmationParameters, 'reservationIds' | 'customEmail' | 'sendEmail'>
    ): CommanderThunkAction<AsyncAction> =>
    async (dispatch, getState, { repositories }) => {
        const promise = repositories.reservations.confirm(params);
        return dispatch(createFetchAction(RESERVATION_CONFIRMED, promise));
    };

export const cancelReservation =
    ({
        reservationIds,
        cancellationReason,
        applyCancellationFee,
        reasonDetails,
        sendEmail,
    }: $TSFixMe): CommanderThunkAction<AsyncAction> =>
    async (dispatch, getState, { container }) => {
        const isCovid = isEqual(cancellationReason, COVID_19_VALUE);

        const normalizedCancellationReason = isCovid ? CancellationReason.Other.value : cancellationReason;

        const cancellationDetails = isCovid ? join(' ', [COVID_19_LABEL, reasonDetails]) : reasonDetails;

        const { reservationComponent } = container();
        const promise = reservationComponent
            .cancel(reservationIds, normalizedCancellationReason, applyCancellationFee, cancellationDetails, sendEmail)
            .then(() => {
                const state = getState();
                const millisecondDuration = getCurrentEnterpriseEditableHistoryIntervalMilliseconds(state);

                const latestUndoUtc = DateTimeUtcService.toISOString(
                    DateTimeUtcService.add(DateTimePart.Milliseconds)(
                        DateTimeUtcService.parse(getNowUtc()),
                        millisecondDuration
                    )
                );

                showSuccessAlert({
                    description: lt('ReservationCancelSuccessMessageNew', {
                        date: <LocalizedDateFromUtc utcString={latestUndoUtc} />,
                        time: <LocalizedShortTimeFromUtc utcString={latestUndoUtc} />,
                    }),
                });
            });
        return dispatch(createFetchAction(RESERVATION_CANCELLED, promise));
    };

export const checkOut =
    ({
        reservationIds,
        closeBalancedBills,
        checkOutWithUnbalancedBill,
        reason,
    }: CheckOutFormValues & { reservationIds: string[] }): CommanderThunkAction<AsyncAction> =>
    async (dispatch, getState, { container }) => {
        const { reservationComponent } = container();
        const promise = reservationComponent
            .checkOut(reservationIds, closeBalancedBills, checkOutWithUnbalancedBill, reason)
            .then(() => {
                dispatch(replace(setUrlParams({ Checkout: 'Succeeded' })));
            })
            .catch((err: $TSFixMe) => {
                dispatch(replace(setUrlParams({ Checkout: 'Failed' })));
                throw err;
            });
        return dispatch(createFetchAction(CHECK_OUT, promise));
    };

export const updateReservationPurpose =
    ({ reservationIds, reservationPurpose }: $TSFixMe): CommanderThunkAction =>
    (dispatch) => {
        const reservationUpdates = map(
            (reservationId) => ({ reservationId, purpose: { value: reservationPurpose } }),
            reservationIds
        );

        return dispatch(updateCurrentEnterpriseReservations({ reservationUpdates }));
    };
export const setReservationsSkipCleaning =
    (reservationIds: $TSFixMe, skipCleaning: $TSFixMe): CommanderThunkAction<AsyncAction> =>
    (dispatch) => {
        const reservationUpdates = map(
            (reservationId) => ({ reservationId, skipCleaning: { value: skipCleaning } }),
            reservationIds
        );

        return dispatch(updateCurrentEnterpriseReservations({ reservationUpdates }));
    };
export const skipTodayCleaning =
    (resourceIds: string[]): CommanderThunkAction<Promise<unknown>> =>
    async (dispatch) =>
        dispatch(
            updateResourcesState({
                resourceIds,
                state: ResourceState.Inspected,
                stateReasonClassification: ResourceStateReasonClassification.SkipCleaning,
                stateReason: null,
            })
        );
export const setInspected =
    (resourceIds: $TSFixMe): CommanderThunkAction<Promise<unknown>> =>
    async (dispatch) =>
        dispatch(
            updateResourcesState({
                resourceIds,
                state: ResourceState.Inspected,
                stateReason: null,
            })
        );

export const setDirty =
    (resourceIds: string[]): CommanderThunkAction<Promise<unknown>> =>
    async (dispatch) =>
        dispatch(
            updateResourcesState({
                resourceIds,
                state: ResourceState.Dirty,
                stateReason: null,
            })
        );

export const setResevationResourcesInspected =
    ({ reservationIds, skipInspectedResources = true }: $TSFixMe): CommanderThunkAction<Promise<unknown>> =>
    async (dispatch, getState) => {
        const state = getState();
        const enterpriseId = getCurrentEnterpriseId();
        const resourceIds = getCurrentEnterpriseReservationsAssignedResourceIds(state, { ids: reservationIds });

        const filteredResourceIds = skipInspectedResources
            ? pipe(
                  getResourcesByIds(state, { ids: resourceIds, enterpriseId }),
                  array.filterMap(({ state: resourceState, id }) =>
                      resourceState !== ResourceState.Inspected ? option.some(id) : option.none
                  )
              )
            : resourceIds;
        return dispatch(setInspected(filteredResourceIds));
    };
export const skipReservationsTodayCleaning =
    (reservationIds: string[]): CommanderThunkAction<Promise<unknown>> =>
    async (dispatch, getState) => {
        const resourceIds = getCurrentEnterpriseReservationsAssignedResourceIds(getState(), { ids: reservationIds });
        return dispatch(skipTodayCleaning(resourceIds));
    };

export const createDeposit =
    ({ reservationId }: $TSFixMe): CommanderThunkAction =>
    (dispatch, getState, { resolveRoute }) => {
        const url = resolveRoute(RouteName.ReservationCreateDeposit, { reservationId });

        dispatch(openModal(url));
    };
const toggleReservationOpenedPure = ({ reservationId, scope }: { reservationId: string; scope: string }) =>
    createAction(RESERVATION_TOGGLE_OPENED, reservationId, { scope });

const setReservationsOpenedPure = ({
    reservationIds,
    isOpened,
    scope,
}: {
    reservationIds: string[];
    isOpened: boolean;
    scope: string;
}) => createAction(RESERVATIONS_SET_OPENED, { reservationIds, isOpened }, { scope });

const toggleReservationCheckedPure = ({ reservationId, scope }: { reservationId: string; scope: string }) =>
    createAction(RESERVATION_TOGGLE_CHECKED, reservationId, { scope });

const setReservationsCheckedPure = ({
    reservationIds,
    isChecked,
    scope,
}: {
    reservationIds: string[];
    isChecked: boolean;
    scope: string;
}) => createAction(RESERVATIONS_SET_CHECKED, { reservationIds, isChecked }, { scope });

const fetchBillsByReservationIds =
    ({ reservationIds }: $TSFixMe): CommanderThunkAction =>
    async (dispatch, getState) => {
        const state = getState();
        const billIds = getReservationsOrderItemsBillIds(state, { serviceOrderIds: reservationIds });

        return notEmpty(billIds) ? dispatch(fetchCurrentEnterpriseBillsByIds({ billIds })) : Promise.resolve();
    };

const loadCanceledReservationsOrderItems =
    ({ scope }: $TSFixMe): CommanderThunkAction =>
    (dispatch, getState) =>
        dispatch(
            fetchCurrentEnterpriseReservationsByGroupIds({
                groupIds: [scope],
                states: [ServiceOrderState.Canceled],
                extent: RESERVATION_ORDER_ITEMS_EXTENT,
            })
        )
            .then(() => dispatch(setCanceledReservationsDataLoaded(scope, true)))
            .then(() => {
                const reservationIds = getCurrentEnterpriseReservationIdsByGroupId(getState(), { groupId: scope });
                return dispatch(fetchBillsByReservationIds({ reservationIds }));
            });

const ensureCanceledReservationsDataLoaded =
    ({ reservationIds, scope }: $TSFixMe): CommanderThunkAction =>
    async (dispatch, getState) => {
        const state = getState();
        const isSomeReservationCanceled = isAnyCurrentEnterpriseReservationCancelled(state, { ids: reservationIds });
        const areCanceledReservationsLoaded = areCanceledReservationDataLoaded(state, { scope });
        const shouldLoad = isSomeReservationCanceled && !areCanceledReservationsLoaded;
        return shouldLoad ? dispatch(loadCanceledReservationsOrderItems({ scope })) : Promise.resolve();
    };

export const toggleReservationOpened =
    ({ reservationId, scope }: $TSFixMe): CommanderThunkAction =>
    (dispatch) => {
        dispatch(toggleReservationOpenedPure({ reservationId, scope }));
        return dispatch(ensureCanceledReservationsDataLoaded({ reservationIds: [reservationId], scope }));
    };

export const setReservationsOpened =
    ({ reservationIds, isOpened, scope }: $TSFixMe): CommanderThunkAction =>
    (dispatch) => {
        dispatch(setReservationsOpenedPure({ reservationIds, isOpened, scope }));
    };

export const toggleReservationSelectionChecked =
    ({ reservationIds, scope, areAllChecked, isAnyChecked }: $TSFixMe): CommanderThunkAction =>
    (dispatch) => {
        const isChecked = !(areAllChecked && isAnyChecked);
        return dispatch(setReservationsChecked({ reservationIds, isChecked, scope }));
    };
export const toggleReservationChecked =
    ({ reservationId, scope, withInit = true }: $TSFixMe): CommanderThunkAction =>
    async (dispatch, getState, { navigate }) => {
        dispatch(toggleReservationCheckedPure({ reservationId, scope }));
        const state = getState();
        const checkedReservationIds = getCheckedReservationIds(state, { scope });
        void navigate({
            to: '.',
            search: {
                reservationIds: compressToBase64(checkedReservationIds.join(',')),
            },
        });
        const stateTabPromise = withInit
            ? dispatch(initializeStateTab({ scope, skipLoadedBalances: true }))
            : Promise.resolve();
        return Promise.all([
            stateTabPromise,
            dispatch(ensureCanceledReservationsDataLoaded({ reservationIds: [reservationId], scope })),
        ]);
    };

export const setReservationsChecked =
    ({ reservationIds, isChecked, scope, withInit = true }: $TSFixMe): CommanderThunkAction =>
    async (dispatch, getState, { navigate }) => {
        dispatch(setReservationsCheckedPure({ reservationIds, isChecked, scope }));
        const state = getState();
        const checkedReservationIds = getCheckedReservationIds(state, { scope });
        void navigate({
            to: '.',
            search: {
                reservationIds: compressToBase64(checkedReservationIds.join(',')),
            },
        });
        return withInit ? dispatch(initializeStateTab({ scope, skipLoadedBalances: true })) : Promise.resolve();
    };
export const refetchGroupAndShowSuccessFlash = ({
    refetchGroup = noop,
    messageKey,
}: {
    refetchGroup: $TSFixMe;
    messageKey?: Mews.TextKey;
}): void => {
    if (messageKey) {
        showSuccessAlert({ description: lt(messageKey) });
    }
    return refetchGroup();
};
export const deleteCompanionship =
    ({ refetchGroup, companionshipId }: $TSFixMe): CommanderThunkAction =>
    async (dispatch) => {
        await dispatch(deleteCompanionshipAction(companionshipId));
        refetchGroupAndShowSuccessFlash({ refetchGroup, messageKey: TextKey.CompanionRemoved });
    };

export const openBillingAccount =
    ({ accountId }: $TSFixMe): CommanderThunkAction =>
    (dispatch, getState) => {
        const url = getBillingAccountUrl(getState(), { accountId });
        return dispatch(openModal(url));
    };

export const openPayments =
    ({ accountId }: $TSFixMe): CommanderThunkAction =>
    (dispatch, getState, { resolveRoute }) => {
        const url = resolveRoute(RouteName.PaymentIndex, { accountId });
        return dispatch(openModal(url));
    };

export const toggleShowAllReservationNotes = (serviceOrderId: string, scope: string) =>
    createAction(RESERVATION_TOGGLE_SHOW_ALL_NOTES, serviceOrderId, { scope });

export const setCanceledReservationsDataLoaded = (scope: string, areLoaded: boolean) =>
    createAction(RESERVATION_LOAD_CANCELED, areLoaded, { scope });

export const setNoteIdsToBeDeleted = ({ scope, noteIds }: { scope: string; noteIds: string[] }) =>
    createAction(SET_NOTE_IDS_TO_BE_DELETED, noteIds, { scope });
export const resetNoteIdsToBeDeleted = (scope: string) => setNoteIdsToBeDeleted({ scope, noteIds: [] });

export const openTimeLine =
    ({ reservationId, serviceId, inNewTab: openInNewTab }: $TSFixMe): CommanderThunkAction =>
    (dispatch, getState) => {
        dispatch(trackReservationModuleEvent({ eventAction: 'openTimeline' }));

        if (openInNewTab) {
            // @ts-expect-error $TSFixMeError: Argument of type '{ reservationId: any; serviceId: any; }' is not assignable to parameter of type '{ reservationId: stri...
            const url = getCurrentEnterpriseTimelineUrl(getState(), { reservationId, serviceId });
            openInBrowserTab(url);
        } else {
            dispatch(showTimeline({ reservationId, serviceId }));
        }
    };
export const initializeStateTab =
    ({ scope, skipLoadedBalances = false }: WithScope & { skipLoadedBalances?: boolean }): CommanderThunkAction =>
    async (dispatch, getState) => {
        const state = getState();
        const loadedBalancesCustomerIds = getLoadedBalancesCustomerIds(state);
        const customerIds = getCheckedReservationCustomerIds(state, { scope });
        const finalCustomerIds = skipLoadedBalances
            ? array.difference(string.Eq)(customerIds, loadedBalancesCustomerIds)
            : customerIds;
        const enterpriseId = getCurrentEnterpriseId();
        const checkedReservationIds = getCheckedReservationIds(state, { scope });

        return isEmpty(checkedReservationIds)
            ? Promise.resolve()
            : Promise.all([
                  dispatch(fetchTasks(checkedReservationIds)),
                  dispatch(fetchCustomerBalances(finalCustomerIds, enterpriseId)),
              ]);
    };

export const handleNewReservation =
    ({ groupId }: $TSFixMe): CommanderThunkAction =>
    (dispatch, getState) => {
        const state = getState();
        const scope = groupId;

        const checkedReservations = getCheckedReservations(state, { scope });
        const checkedReservationIds = map('id', checkedReservations);

        const getCommonPropertyValue = (propertyName: $TSFixMe) =>
            pipe(checkedReservations, getItemByCommonPropertyValue(propertyName));

        const serviceId = getCommonPropertyValue('serviceId');
        const productIds = serviceId
            ? getReservationsCommonProductIds(state, {
                  // @ts-expect-error $TSFixMeError: Argument of type '{ scope: any; serviceId: any; serviceOrderIds: string[]; }' is not assignable to parameter of type 'ob...
                  scope,
                  serviceId,
                  serviceOrderIds: checkedReservationIds,
              })
            : [];

        const purposeEnumValue = getCommonPropertyValue('purpose');

        const url = getReservationUrl(state, {
            ReleasedUtc: getCommonPropertyValue('releasedUtc') || '',
            GroupId: groupId,
            CustomerId: getCommonPropertyValue('customerId') || '',
            StartUtc: getCommonPropertyValue('startUtc') || '',
            EndUtc: getCommonPropertyValue('endUtc') || '',
            State: getCommonPropertyValue('serviceOrderState') || '',
            Origin: getCommonPropertyValue('commanderOrigin') || '',
            // @ts-expect-error $TSFixMeError: Object is possibly 'undefined'.
            Purpose: purposeEnumValue ? ReservationPurpose.getByValue(purposeEnumValue).name : '',
            PartnerCompanyId: getCommonPropertyValue('partnerCompanyId') || '',
            TravelAgencyId: getCommonPropertyValue('travelAgencyId') || '',
            BookerId: getCommonPropertyValue('bookerId') || '',
            ProductIds: notEmpty(productIds) ? productIds.join(',') : '',
            ServiceId: serviceId || '',
            AvailabilityBlockId: getCommonPropertyValue('availabilityBlockId') || '',
        });

        dispatch(openModal(url));
        dispatch(trackReservationModuleEvent({ eventAction: 'newReservation' }));
    };

export const fetchCurrentEnterpriseCheckedReservations =
    ({ scope, extent = RESERVATION_FULL_DETAIL_EXTENT }: $TSFixMe): CommanderThunkAction =>
    async (dispatch, getState) => {
        const checkedReservationIds = getCheckedReservationIds(getState(), { scope });
        return dispatch(
            fetchCurrentEnterpriseReservationsByReservationIds({
                reservationIds: checkedReservationIds,
                extent,
            })
        );
    };

export const fetchGroupNonCanceledReservations = ({ groupId, extent = RESERVATION_FULL_DETAIL_EXTENT }: $TSFixMe) =>
    fetchCurrentEnterpriseReservationsByGroupIds({
        groupIds: [groupId],
        extent,
        states: [
            ServiceOrderState.Requested,
            ServiceOrderState.Inquired,
            ServiceOrderState.Optional,
            ServiceOrderState.Confirmed,
            ServiceOrderState.Started,
            ServiceOrderState.Processed,
        ],
    });

const fetchOrderItemsByGroupId =
    ({ groupId }: $TSFixMe): CommanderThunkAction =>
    (dispatch, getState) =>
        dispatch(fetchGroupNonCanceledReservations({ groupId, extent: RESERVATION_ORDER_ITEMS_EXTENT })).then(() => {
            const reservationIds = getCurrentEnterpriseReservationIdsByGroupId(getState(), { groupId });
            return dispatch(fetchBillsByReservationIds({ reservationIds }));
        });

const initializeReservationModuleService =
    ({ groupId, enterpriseId }: $TSFixMe): CommanderThunkAction =>
    async (dispatch, getState) => {
        const serviceIds = getReservationsGroupServiceIds(getState(), { enterpriseId, groupId });
        const serviceDeletionState = EntityDeletionState.Everything;
        return Promise.all([
            dispatch(fetchRates({ serviceIds, serviceDeletionState })),
            dispatch(fetchKeyCutters({ enterpriseId })),
            dispatch(
                fetchAllEnterpriseServices({
                    enterpriseIds: [enterpriseId],
                    serviceIds,
                    extent: {
                        businessSegments: true,
                        products: true,
                    },
                })
            ),
        ]);
    };
const fetchCardsByAccountIdsChecked =
    (accountIds: $TSFixMe): CommanderThunkAction =>
    (dispatch) =>
        notEmpty(accountIds) ? dispatch(fetchCardsByAccountIds({ accountIds })) : Promise.resolve();

const fetchAccountNotesByAccountIdsChecked =
    (accountIds: string[]): CommanderThunkAction =>
    async (dispatch) =>
        notEmpty(accountIds) ? dispatch(fetchAccountNotes({ accountIds })) : Promise.resolve();

const fetchInitialReservationGroupData =
    ({ groupId, reservationIds }: $TSFixMe): CommanderThunkAction =>
    async (dispatch) => {
        const data = await dispatch(
            fetchCurrentEnterpriseReservationsByGroupIds({
                groupIds: [groupId],
                extent: RESERVATION_DATA_EXTENT,
            })
        );
        dispatch(deselectAllReservations({ scope: groupId }));
        dispatch(ensureCanceledReservationsDataLoaded({ reservationIds, scope: groupId }));
        dispatch(setReservationsChecked({ reservationIds, scope: groupId, isChecked: true, withInit: false }));
        dispatch(setReservationsOpened({ reservationIds, scope: groupId, isOpened: true }));
        return data;
    };
export const initializeReservationModule =
    ({
        groupId,
        enterpriseId,
        reservationIds,
    }: WithGroupId & WithEnterpriseId & { reservationIds: string[] }): CommanderThunkAction =>
    async (dispatch: $TSFixMe, getState) => {
        const startTime = window.performance.now();
        const reservationsPromise = dispatch(fetchInitialReservationGroupData({ groupId, reservationIds }));
        const data = await dispatch(createFetchAction(RESERVATIONS_FETCH, reservationsPromise, { scope: groupId }));
        const initialPromiseTime = window.performance.now();
        const { accounts } = data.value.value;
        const orderItemsPromise = dispatch(fetchOrderItemsByGroupId({ groupId }));
        await Promise.all([
            dispatch(createFetchAction(ORDER_ITEMS_FETCH, orderItemsPromise, { scope: groupId })),
            dispatch(initializeStateTab({ scope: groupId })),
            dispatch(fetchCardsByAccountIdsChecked(mapId(accounts))),
            dispatch(fetchAccountNotesByAccountIdsChecked(mapId(accounts))),
            dispatch(initializeReservationModuleService({ groupId, enterpriseId })),
        ]);

        const endTime = window.performance.now();

        const eventValue = {
            totalTime: endTime - startTime,
            initialTime: initialPromiseTime - startTime,
            groupId,
            groupSize: getCurrentEnterpriseReservationIdsByGroupId(getState(), { groupId }).length,
        };

        // @ts-expect-error $TSFixMeError: Type '{ totalTime: number; initialTime: number; }' is not assignable to type 'string'.
        dispatch(trackReservationModuleEvent({ eventAction: 'reservationModuleInitialized', eventValue }));
    };

const deselectAllReservations = ({ scope }: $TSFixMe) => createAction(RESERVATION_STATE_CLEANED, undefined, { scope });

export const refetchReservations =
    ({ reservationIds, scope, extent = RESERVATION_FULL_DETAIL_EXTENT }: $TSFixMe): CommanderThunkAction =>
    async (dispatch) =>
        dispatch(fetchCurrentEnterpriseReservationsByReservationIds({ reservationIds, extent })).then(
            ({ value: { accounts } }) =>
                dispatch(fetchChangedReservationModuleEntities(scope, reservationIds, mapId(accounts)))
        );

export const refetchCheckedReservations =
    (scope: $TSFixMe): CommanderThunkAction =>
    (dispatch, getState) => {
        const reservationIds = getCheckedReservationIds(getState(), { scope });
        return dispatch(refetchReservations({ reservationIds, scope }));
    };
export const refetchReservationsByGroupId =
    ({ groupId, extent = RESERVATION_FULL_DETAIL_EXTENT }: $TSFixMe): CommanderThunkAction =>
    (dispatch) =>
        dispatch(fetchCurrentEnterpriseReservationsByGroupIds({ groupIds: [groupId], extent })).then(
            ({ value: { accounts, reservations } }: $TSFixMe) =>
                dispatch(fetchChangedReservationModuleEntities(groupId, mapId(reservations), mapId(accounts)))
        );

const fetchChangedReservationModuleEntities =
    (groupId: $TSFixMe, reservationIds: $TSFixMe, accountIds: $TSFixMe): CommanderThunkAction =>
    async (dispatch) =>
        Promise.all([
            dispatch(initializeStateTab({ scope: groupId })),
            dispatch(fetchBillsByReservationIds({ reservationIds })),
            dispatch(fetchCardsByAccountIdsChecked(accountIds)),
        ]);
